import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  CssBaseline,
  Breadcrumbs,
  Card,
  Stack,
  CircularProgress,
} from '@mui/material';
import Header from '../Includes/Header';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import AppToast from '../../components/Toast/AppToast';
import { ArrowBack } from '@mui/icons-material';
import { Link } from "react-router-dom";  

const CreateExercise = () => {
  const { colors: appColors } = useAppTheme();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    exercise_name: '',
    description: '',
    instructions: '',
    sets: '',
    reps: '',
    duration: '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const getToken = () => JSON.parse(localStorage.getItem('userdata') || '{}')?.token;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.exercise_name?.trim()) {
      setSnackbar({ open: true, message: 'Exercise name is required', severity: 'error' });
      return;
    }
    const token = getToken();
    if (!token) return;
    setLoading(true);
    try {
      const body = {
        exercise_name: form.exercise_name.trim(),
      };
      if (form.description?.trim()) body.description = form.description.trim();
      if (form.instructions?.trim()) body.instructions = form.instructions.trim();
      if (form.sets?.trim()) body.sets = form.sets.trim();
      if (form.reps?.trim()) body.reps = form.reps.trim();
      if (form.duration?.trim()) body.duration = form.duration.trim();

      const res = await fetch(`${window.constants.api_url}/create/exercise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.code === 200) {
        setSnackbar({ open: true, message: data.message || 'Exercise created successfully', severity: 'success' });
        setForm({ exercise_name: '', description: '', instructions: '', sets: '', reps: '', duration: '' });
        navigate('/exercise-list');
      } else {
        setSnackbar({ open: true, message: data.message || 'Failed to create exercise', severity: 'error' });
      }
    } catch (e) {
      console.error('Error creating exercise:', e);
      setSnackbar({ open: true, message: 'Something went wrong', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <CssBaseline />
      <Header />
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, mt: 8 }}>
        <Container maxWidth="md">
          <Card sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <Button startIcon={<ArrowBack />} onClick={() => navigate('/exercise-list')} sx={{ color: appColors.evergreen, textTransform: 'none' }}>
                Back
              </Button>
              <Box>
                <Typography variant="h5" sx={{ color: appColors.evergreen, fontWeight: 600 }}>Create Exercise</Typography>
                <Breadcrumbs sx={{ mt: 0.5 }}>
                  <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/exercise-list'); }}>Exercise</Link>
                  <Typography color="text.primary">Create Exercise</Typography>
                </Breadcrumbs>
              </Box>
            </Stack>

            <Stack spacing={2}>
              <TextField
                required
                fullWidth
                name="exercise_name"
                label="Exercise name"
                value={form.exercise_name}
                onChange={handleChange}
                variant="outlined"
              />
              <TextField
                fullWidth
                name="description"
                label="Description"
                value={form.description}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                name="instructions"
                label="Instructions"
                value={form.instructions}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={3}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField fullWidth name="sets" label="Sets" value={form.sets} onChange={handleChange} variant="outlined" />
                <TextField fullWidth name="reps" label="Reps" value={form.reps} onChange={handleChange} variant="outlined" />
                <TextField fullWidth name="duration" label="Duration" value={form.duration} onChange={handleChange} variant="outlined" placeholder="e.g. 30 sec" />
              </Stack>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/exercise-list')} sx={{ color: appColors.evergreen, borderColor: appColors.evergreen, textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={loading} sx={{ bgcolor: appColors.evergreen, '&:hover': { bgcolor: appColors.pine }, textTransform: 'none' }}>
                  {loading ? <CircularProgress size={24} /> : 'Create'}
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Container>
      </Box>

      <AppToast open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} />
    </Box>
  );
};

export default CreateExercise;
